/* ============================================
   AnalyticsDashboard — Resumen analítico
   Consigue métricas reales desde Metricool
   ============================================ */
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import {
  IconBarChart, IconTrendingUp, IconEye, IconTarget,
} from '@/components/icons/Icons'
import type { SocialPost, SocialChannel, SocialPostStatus } from '@/lib/types'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface AnalyticsDashboardProps {
  posts: SocialPost[]
}

const CHANNEL_CONFIG: Record<SocialChannel, { label: string; color: string }> = {
  instagram: { label: 'Instagram', color: '#E4405F' },
  facebook: { label: 'Facebook', color: '#1877F2' },
  twitter: { label: 'Twitter/X', color: '#A0A0A0' },
  pinterest: { label: 'Pinterest', color: '#E60023' },
}

const STATUS_LABELS: Record<SocialPostStatus, { label: string; cssClass: string }> = {
  proposal: { label: 'Propuesta IA', cssClass: 'badge-info' },
  draft: { label: 'Borrador', cssClass: 'badge-borrador' },
  pending_review: { label: 'En revisión', cssClass: 'badge-enviada' },
  approved: { label: 'Aprobado', cssClass: 'badge-aceptada' },
  scheduled: { label: 'Programado', cssClass: 'badge-info' },
  published: { label: 'Publicado', cssClass: 'badge-aceptada' },
  rejected: { label: 'Rechazado', cssClass: 'badge-rechazada' },
  error: { label: 'Error', cssClass: 'badge-rechazada' },
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  return text.slice(0, max) + '…'
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('es-PE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

// ─── Custom SVG Reach Chart ───
function ReachChart({ points }: { points: { date: string; value: number }[] }) {
  const width = 500
  const height = 150
  const padding = 20

  if (points.length === 0) {
    return (
      <div className="analytics-chart-empty">
        No hay datos de alcance disponibles para este período.
      </div>
    )
  }

  const values = points.map(p => p.value)
  const maxVal = Math.max(...values, 1)

  const chartWidth = width - padding * 2
  const chartHeight = height - padding * 2
  const pointsCount = points.length

  const coords = points.map((p, idx) => {
    const x = padding + (idx / (pointsCount - 1 || 1)) * chartWidth
    const y = padding + chartHeight - (p.value / maxVal) * chartHeight
    return { x, y }
  })

  let linePath = `M ${coords[0].x} ${coords[0].y}`
  for (let i = 1; i < coords.length; i++) {
    linePath += ` L ${coords[i].x} ${coords[i].y}`
  }

  const areaPath = `${linePath} L ${coords[coords.length - 1].x} ${height - padding} L ${coords[0].x} ${height - padding} Z`

  const formatDateLabel = (dStr: string) => {
    if (dStr.length !== 8) return dStr
    const mNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic']
    const month = Number(dStr.slice(4, 6)) - 1
    const day = Number(dStr.slice(6, 8))
    return `${day} ${mNames[month] || ''}`
  }

  return (
    <div className="analytics-chart-container">
      <svg viewBox={`0 0 ${width} ${height}`} className="analytics-chart">
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#342CDE" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#342CDE" stopOpacity="0" />
          </linearGradient>
        </defs>

        <line x1={padding} y1={padding} x2={width - padding} y2={padding} stroke="var(--veil-10)" />
        <line x1={padding} y1={padding + chartHeight / 2} x2={width - padding} y2={padding + chartHeight / 2} stroke="var(--veil-10)" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="var(--border)" strokeWidth="1.5" />

        <path d={areaPath} fill="url(#chartGrad)" />
        <path d={linePath} fill="none" stroke="#342CDE" strokeWidth="2.5" />

        {coords.map((c, idx) => (
          <g key={idx} className="chart-dot-group">
            <circle cx={c.x} cy={c.y} r="3" fill="#342CDE" className="chart-dot" />
            <title>{`${formatDateLabel(points[idx].date)}: ${points[idx].value.toLocaleString('es-PE')} personas`}</title>
          </g>
        ))}
      </svg>
      <div className="analytics-chart-labels">
        <span>{formatDateLabel(points[0].date)}</span>
        <span>{formatDateLabel(points[Math.floor(points.length / 2)].date)}</span>
        <span>{formatDateLabel(points[points.length - 1].date)}</span>
      </div>
    </div>
  )
}

export default function AnalyticsDashboard({ posts }: AnalyticsDashboardProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [analytics, setAnalytics] = useState<any>(null)

  useEffect(() => {
    let isMounted = true
    async function fetchStats() {
      setLoading(true)
      setError(null)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) throw new Error('No session active')

        // Last 30 days
        const end = new Date().toISOString().split('T')[0]
        const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

        const res = await fetch(`${API_URL}/api/social/metricool-proxy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'get-analytics',
            start,
            end
          })
        })

        if (!res.ok) throw new Error(`Error en el servidor de analíticas (${res.status})`)
        const json = await res.json()

        if (isMounted) {
          if (json.ok) {
            setAnalytics(json.data)
          } else {
            setError(json.error || 'Error al obtener estadísticas')
          }
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message || 'Error de conexión')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }
    fetchStats()
    return () => { isMounted = false }
  }, [])

  // ─── Process data ───
  const publishedPosts = posts.filter(p => p.status === 'published')

  let followersCount = 0
  let reachCount = 0
  let engagementRate = 0
  let avgLikes = 0
  const dailyReachPoints: { date: string; value: number }[] = []

  if (analytics) {
    const igF = analytics.igFollowers || []
    const fbF = analytics.fbLikes || [] // facebookLikes
    const latestIgFollowers = igF.length > 0 ? Number(igF[igF.length - 1][1]) || 0 : 0
    const latestFbFollowers = fbF.length > 0 ? Number(fbF[fbF.length - 1][1]) || 0 : 0
    followersCount = latestIgFollowers + latestFbFollowers

    const igR = analytics.igReach || []
    const fbR = analytics.fbReach || [] // dailyImpressionsUnique
    const igReachSum = igR.reduce((sum: number, item: any) => sum + (Number(item[1]) || 0), 0)
    const fbReachSum = fbR.reduce((sum: number, item: any) => sum + (Number(item[1]) || 0), 0)
    reachCount = igReachSum + fbReachSum

    const igE = analytics.igEngagement || []
    const fbE = analytics.fbEngagement || []
    const igEngAvg = igE.length > 0 ? (igE.reduce((sum: number, item: any) => sum + (Number(item[1]) || 0), 0) / igE.length) : 0
    const fbEngAvg = fbE.length > 0 ? (fbE.reduce((sum: number, item: any) => sum + (Number(item[1]) || 0), 0) / fbE.length) : 0
    
    if (igEngAvg > 0 && fbEngAvg > 0) {
      engagementRate = Math.round(((igEngAvg + fbEngAvg) / 2) * 100) / 100
    } else {
      engagementRate = Math.round((igEngAvg || fbEngAvg || 0) * 100) / 100
    }

    const igL = analytics.igLikes || []
    const fbL = analytics.fbLikes || []
    const igLikesSum = igL.reduce((sum: number, item: any) => sum + (Number(item[1]) || 0), 0)
    const fbLikesSum = fbL.reduce((sum: number, item: any) => sum + (Number(item[1]) || 0), 0)
    const totalLikes = igLikesSum + fbLikesSum
    const totalDays = Math.max(igL.length, fbL.length, 1)
    avgLikes = Math.round(totalLikes / totalDays)

    // Combine reach series by date
    const reachByDate: Record<string, number> = {}
    const normalizeDate = (dStr: string) => {
      if (dStr.length > 8) {
        const d = new Date(Number(dStr))
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}${m}${day}`
      }
      return dStr
    }

    igR.forEach((item: any) => {
      const date = normalizeDate(item[0])
      reachByDate[date] = (reachByDate[date] || 0) + (Number(item[1]) || 0)
    })
    fbR.forEach((item: any) => {
      const date = normalizeDate(item[0])
      reachByDate[date] = (reachByDate[date] || 0) + (Number(item[1]) || 0)
    })

    Object.entries(reachByDate).forEach(([date, val]) => {
      dailyReachPoints.push({ date, value: val })
    })
    dailyReachPoints.sort((a, b) => a.date.localeCompare(b.date))
  }

  // ─── Posts by status ───
  const statusCounts = posts.reduce<Record<string, number>>((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1
    return acc
  }, {})

  // ─── Posts by channel ───
  const channelCounts: Record<SocialChannel, number> = {
    instagram: 0,
    facebook: 0,
    twitter: 0,
    pinterest: 0,
  }
  for (const post of posts) {
    for (const ch of (post.channels ?? [])) {
      channelCounts[ch] = (channelCounts[ch] || 0) + 1
    }
  }
  const maxChannelCount = Math.max(...Object.values(channelCounts), 1)

  const recentPublished = [...publishedPosts]
    .sort((a, b) => (b.publication_date ?? '').localeCompare(a.publication_date ?? ''))
    .slice(0, 10)

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="auth-spinner" />
        <p>Sincronizando con Metricool...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="analytics-error card glass-card">
        <p style={{ color: 'var(--danger)', fontWeight: 600 }}>Error al cargar analíticas</p>
        <p className="page-subtitle" style={{ marginTop: 4 }}>{error}</p>
      </div>
    )
  }

  return (
    <div className="analytics-dashboard page-enter">
      {/* KPI Grid */}
      <div className="stats-grid">
        <div className="stat-card glass-card">
          <div className="stat-label">Seguidores Totales</div>
          <div className="stat-value">{followersCount.toLocaleString('es-PE')}</div>
        </div>
        <div className="stat-card glass-card">
          <div className="stat-label">
            <IconTrendingUp size={14} /> Engagement Rate
          </div>
          <div className="stat-value success">{engagementRate}%</div>
        </div>
        <div className="stat-card glass-card">
          <div className="stat-label">
            <IconEye size={14} /> Alcance Total (30d)
          </div>
          <div className="stat-value info">
            {reachCount >= 1000 ? `${(reachCount / 1000).toFixed(1)}K` : reachCount}
          </div>
        </div>
        <div className="stat-card glass-card">
          <div className="stat-label">
            <IconTarget size={14} /> Likes Promedio
          </div>
          <div className="stat-value warning">{avgLikes}</div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="card glass-card" style={{ marginTop: 20 }}>
        <div className="card-header">
          <h3>
            <IconBarChart size={16} /> Alcance Diario Consolidado (Instagram + Facebook)
          </h3>
        </div>
        <div className="card-body">
          <ReachChart points={dailyReachPoints} />
        </div>
      </div>

      <div className="analytics-dashboard__grid" style={{ marginTop: 20 }}>
        {/* Posts by Status */}
        <div className="card glass-card">
          <div className="card-header">
            <h3>Posts por Estado (Local)</h3>
          </div>
          <div className="analytics-status-grid">
            {(Object.entries(STATUS_LABELS) as [SocialPostStatus, typeof STATUS_LABELS[SocialPostStatus]][]).map(
              ([status, config]) => {
                const count = statusCounts[status] || 0
                return (
                  <div key={status} className="analytics-status-row">
                    <span className={`badge ${config.cssClass}`}>{config.label}</span>
                    <span className="analytics-status-count">{count}</span>
                  </div>
                )
              }
            )}
          </div>
        </div>

        {/* Posts by Channel */}
        <div className="card glass-card">
          <div className="card-header">
            <h3>
              <IconBarChart size={16} /> Posts por Canal
            </h3>
          </div>
          <div className="analytics-channels">
            {(Object.entries(CHANNEL_CONFIG) as [SocialChannel, typeof CHANNEL_CONFIG[SocialChannel]][]).map(
              ([channel, config]) => {
                const count = channelCounts[channel]
                const pct = (count / maxChannelCount) * 100
                return (
                  <div key={channel} className="analytics-channel-row">
                    <span className="analytics-channel-label">{config.label}</span>
                    <div className="analytics-channel-bar-track">
                      <div
                        className="analytics-channel-bar"
                        style={{
                          width: `${pct}%`,
                          background: config.color,
                        }}
                      />
                    </div>
                    <span className="analytics-channel-count">{count}</span>
                  </div>
                )
              }
            )}
          </div>
        </div>
      </div>

      {/* Recent Published Posts */}
      {recentPublished.length > 0 && (
        <div className="card glass-card" style={{ marginTop: 20 }}>
          <div className="card-header">
            <h3>Publicaciones Recientes</h3>
          </div>
          <div className="table-container" style={{ border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Caption</th>
                  <th>Canales</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {recentPublished.map(post => (
                  <tr key={post.id}>
                    <td>{formatDate(post.publication_date)}</td>
                    <td>{truncate(post.caption, 60)}</td>
                    <td>
                      <div className="analytics-channel-chips">
                        {(post.channels ?? []).map(ch => (
                          <span
                            key={ch}
                            className="analytics-channel-chip"
                            style={{ background: CHANNEL_CONFIG[ch]?.color }}
                          >
                            {CHANNEL_CONFIG[ch]?.label || ch}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_LABELS[post.status]?.cssClass || ''}`}>
                        {STATUS_LABELS[post.status]?.label || post.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
